import type { AtlasTool } from '@atlas-control/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Fragment, useDeferredValue, useState } from 'react';
import { PageHeader } from '@/components/page-header';
import { StatusBadge } from '@/components/status-badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { guard } from '@/lib/guards';

export const Route = createFileRoute('/_app/tools/')({
  beforeLoad: ({ context }) => guard(context.me, 'runs.read'),
  component: ToolsPage,
});

/** Every tool a finished run proposed, ours or external, with the features it was chosen for. */
function ToolsPage() {
  const [q, setQ] = useState('');
  const search = useDeferredValue(q.trim());
  const tools = useQuery({
    queryKey: ['library', 'tools', search],
    queryFn: () =>
      api<{ tools: AtlasTool[] }>('/library/tools', { query: { q: search || undefined } }).then(
        (r) => r.tools,
      ),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      <PageHeader title="Tool archive" />
      <Input
        type="search"
        placeholder="Search by name"
        aria-label="Search tools"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="mb-3 max-w-sm"
      />
      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tool</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Uses</TableHead>
              <TableHead className="text-right">Runs</TableHead>
              <TableHead>Last seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tools.isError && (
              <TableRow>
                <TableCell colSpan={5} className="text-danger">
                  The tool archive is unavailable (Atlas did not answer).
                </TableCell>
              </TableRow>
            )}
            {tools.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground">
                  {search ? 'No tool matches.' : 'No finished run has proposed a tool yet.'}
                </TableCell>
              </TableRow>
            )}
            {tools.data?.map((t) => (
              <Fragment key={t.tool_key}>
                <TableRow>
                  <TableCell>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-left font-medium"
                      aria-expanded={open.has(t.tool_key)}
                      onClick={() => toggle(t.tool_key)}
                    >
                      <ChevronRight
                        className={`size-4 transition-transform ${open.has(t.tool_key) ? 'rotate-90' : ''}`}
                      />
                      {t.name}
                    </button>
                    {t.url && /^https?:\/\//.test(t.url) && (
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="ml-6 inline-flex items-center gap-1 text-xs text-info underline-offset-4 hover:underline"
                      >
                        {t.url} <ExternalLink className="size-3" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    {t.source === 'catalog' ? (
                      t.product_id ? (
                        <Link to="/catalog" hash={t.product_id} className="hover:underline">
                          <StatusBadge tone="info">Ours</StatusBadge>
                        </Link>
                      ) : (
                        <StatusBadge tone="neutral">Ours (product deleted)</StatusBadge>
                      )
                    ) : (
                      <StatusBadge tone="neutral">External</StatusBadge>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{t.uses}</TableCell>
                  <TableCell className="text-right tabular-nums">{t.runs}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(t.last_seen)}</TableCell>
                </TableRow>
                {open.has(t.tool_key) && (
                  <TableRow>
                    <TableCell colSpan={5} className="bg-muted/40">
                      <ul className="list-disc space-y-0.5 pl-10 text-sm">
                        {t.features.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
